import { useState, useEffect, useRef } from 'react';
import { fetchProjectDetails, handleUpdateTitle, handleUpdateDescription } from '../components/VideoEditor/projectHandlers';
import { isProjectSegmented } from '../utils/videoSegmentUtils';

interface UseProjectDataProps {
  projectId: string;
  showNotification: (notification: any) => void;
  setDisabledBtnPendingDownload: (disabled: boolean) => void;
}

export const useProjectData = ({ projectId, showNotification, setDisabledBtnPendingDownload }: UseProjectDataProps) => {
  const [project, setProject] = useState<any>(null);
  const [projectTitle, setProjectTitle] = useState<string>("Untitled Project");
  const [projectDescription, setProjectDescription] = useState<string>("");
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const [isVideoSegmented, setIsVideoSegmented] = useState(false);
  const downloadLink = useRef("");

  // Load project details when projectId changes
  useEffect(() => {
    if (!projectId) return;

    console.log("📡 [PROJECT LOAD] Fetching details for project:", projectId);

    fetchProjectDetails(projectId)
      .then((projectData) => {
        if (projectData) {
          console.log("✅ [PROJECT LOAD] Received project data:", projectData.title);
          setProject(projectData);
          
          if (projectData.title) {
            console.log("📝 [PROJECT LOAD] Setting title to:", projectData.title);
            setProjectTitle(projectData.title);
          }

          if (projectData.description) {
            setProjectDescription(projectData.description);
          }

          if (projectData.generatedVideoUrl) {
            setGeneratedVideoUrl(projectData.generatedVideoUrl);
            downloadLink.current = projectData.generatedVideoUrl;
          }
          
          // Detect if video is segmented
          if (isProjectSegmented(projectData)) {
            setIsVideoSegmented(true);
            console.log(`📊 Segmented video detected: ${projectData.totalVideoSegments} segments`);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching project details:", error);
      });
  }, [projectId]);

  // ✅ FIX: Update project state whenever generatedVideoUrl changes so VerticalNav sees the latest video
  useEffect(() => {
    if (generatedVideoUrl && project) {
      setProject((prev: any) => ({
        ...prev,
        generatedVideoUrl: generatedVideoUrl
      }));
      console.log('✅ ProjectData updated with new generatedVideoUrl:', generatedVideoUrl);
    }
  }, [generatedVideoUrl]);

  // Update title handler
  const updateTitle = (newTitle: string) => {
    handleUpdateTitle(
      newTitle,
      projectId,
      showNotification,
      setProjectTitle,
      setIsEditingTitle,
      setDisabledBtnPendingDownload
    );
  };

  // Update description handler
  const updateDescription = (newDescription: string) => {
    console.log('📝 updateDescription called in hook:', newDescription);
    handleUpdateDescription(
      newDescription,
      projectId,
      showNotification,
      setProjectDescription,
      setDisabledBtnPendingDownload
    );
  };

  return {
    project,
    setProject,
    projectTitle,
    setProjectTitle,
    projectDescription,
    setProjectDescription,
    isEditingTitle,
    setIsEditingTitle,
    updateTitle,
    updateDescription,
    generatedVideoUrl,
    setGeneratedVideoUrl,
    downloadLink,
    isVideoSegmented,
    setIsVideoSegmented
  };
};
